import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import usuarioService from '../services/UsuarioService';
import materiaService from '../services/MateriaService';
import RegionalService from '../services/RegionalService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import SearchableSelect from './common/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { isGptClassicEnabled as hasGptClassicFeature } from '../utils/adminMenu';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { navigateAdminMenuItem } from './sidebar/menuConfig';

const normalizeId = (value) => (value ? String(value) : '');
const normalizeMaterias = (list) => (Array.isArray(list) ? list.filter(Boolean) : []);
const getMateriaId = (materia) => normalizeId(materia?.id || materia?.Id);
const getMateriaNombre = (materia) => materia?.nombre || materia?.Nombre || materia?.name || materia?.Name || 'Materia';
const getMateriaMeta = (materia) => {
  const area = materia?.nombreArea || materia?.NombreArea;
  const anio = materia?.valorAnio || materia?.ValorAnio;
  return [area, anio ? `${anio}° año` : ''].filter(Boolean).join(' | ');
};
const getLoadedMateriaIds = (list) => normalizeMaterias(list).map(getMateriaId).filter(Boolean);

const AdminEditProfesor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profesorId } = useParams();
  const { user } = useAuth();
  const profesorState = useMemo(() => location.state?.profesor || {}, [location.state]);
  const stateRegionalId = location.state?.idRegional || null;

  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => stateRegionalId || RegionalService.getActiveRegionalId(), [stateRegionalId]);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional';
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(RegionalService.getFeatureFlags()), []);
  const handleAdminMenuSelect = (item) => navigateAdminMenuItem(navigate, item.id);

  const [form, setForm] = useState({
    email: profesorState.email || profesorState.Email || '',
    nombre: profesorState.nombre || profesorState.Nombre || '',
    apellido: profesorState.apellido || profesorState.Apellido || '',
    telefono: profesorState.telefono || profesorState.Telefono || '',
    dni: profesorState.dni || profesorState.Dni || '',
    legajo: profesorState.legajo || profesorState.Legajo || '',
    permiteCrearAsistente: !!(profesorState.permiteCrearAsistente || profesorState.PermiteCrearAsistente),
  });
  const [materiasDisponibles, setMateriasDisponibles] = useState([]);
  const [materiasAsignadas, setMateriasAsignadas] = useState([]);
  const [persistedMateriaIds, setPersistedMateriaIds] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [selectedMateriaId, setSelectedMateriaId] = useState('');
  const [isAssociatingMaterias, setIsAssociatingMaterias] = useState(false);
  const [materiasMessage, setMateriasMessage] = useState('');
  const [materiasMessageType, setMateriasMessageType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState(''); // success | error | info

  const persistedMateriaIdSet = useMemo(
    () => new Set(persistedMateriaIds.map(normalizeId).filter(Boolean)),
    [persistedMateriaIds]
  );

  const assignedMateriaIdSet = useMemo(
    () => new Set(materiasAsignadas.map(getMateriaId).filter(Boolean)),
    [materiasAsignadas]
  );

  const materiasDisponiblesParaAgregar = useMemo(() => (
    materiasDisponibles
      .filter((materia) => {
        const materiaId = getMateriaId(materia);
        return materiaId && !assignedMateriaIdSet.has(materiaId);
      })
      .sort((a, b) => getMateriaNombre(a).localeCompare(getMateriaNombre(b), 'es', { sensitivity: 'base' }))
  ), [assignedMateriaIdSet, materiasDisponibles]);

  const materiaSearchOptions = useMemo(() => (
    materiasDisponiblesParaAgregar.map((materia) => {
      const nombre = getMateriaNombre(materia);
      const meta = getMateriaMeta(materia);

      return {
        value: getMateriaId(materia),
        label: nombre,
        meta,
        searchText: `${nombre} ${meta}`
      };
    })
  ), [materiasDisponiblesParaAgregar]);

  const pendingMateriaIds = useMemo(() => {
    const uniqueIds = Array.from(new Set(materiasAsignadas.map(getMateriaId).filter(Boolean)));
    return uniqueIds.filter((materiaId) => !persistedMateriaIdSet.has(materiaId));
  }, [materiasAsignadas, persistedMateriaIdSet]);

  useEffect(() => {
    if (!profesorId) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [profesorId, navigate]);

  useEffect(() => {
    const fetchProfesor = async () => {
      const hasState = profesorState && Object.keys(profesorState).length > 0;
      const stateHasPermite = profesorState?.permiteCrearAsistente !== undefined || profesorState?.PermiteCrearAsistente !== undefined;
      const stateHasMaterias = profesorState?.materias !== undefined || profesorState?.Materias !== undefined;
      if (hasState && stateHasPermite && stateHasMaterias) return;
      if (!profesorId) return;
      try {
        const prof = await usuarioService.getProfesorById(profesorId, { idRegional: activeRegionalId });
        if (prof) {
          setForm({
            email: prof.email || prof.Email || '',
            nombre: prof.nombre || prof.Nombre || '',
            apellido: prof.apellido || prof.Apellido || '',
            telefono: prof.telefono || prof.Telefono || '',
            dni: prof.dni || prof.Dni || '',
            legajo: prof.legajo || prof.Legajo || '',
            permiteCrearAsistente: prof.permiteCrearAsistente || prof.PermiteCrearAsistente || false,
          });
          const loadedMaterias = normalizeMaterias(prof.materias || prof.Materias);
          setMateriasAsignadas(loadedMaterias);
          setPersistedMateriaIds(getLoadedMateriaIds(loadedMaterias));
        }
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar el profesor.');
        setMessageType('error');
      }
    };
    fetchProfesor();
  }, [activeRegionalId, profesorId, profesorState]);

  useEffect(() => {
    const initialMaterias = normalizeMaterias(profesorState.materias || profesorState.Materias);
    if (initialMaterias.length) {
      setMateriasAsignadas(initialMaterias);
      setPersistedMateriaIds(getLoadedMateriaIds(initialMaterias));
    }
  }, [profesorState]);

  useEffect(() => {
    const fetchMaterias = async () => {
      if (!activeRegionalId) return;
      setMateriasLoading(true);
      setMateriasError('');
      try {
        const list = await materiaService.getMaterias({ idRegional: activeRegionalId });
        const activeRegionalIdText = normalizeId(activeRegionalId);
        const filtradas = Array.isArray(list)
          ? list.filter((m) => {
            const materiaRegionalId = normalizeId(m.idRegional || m.IdRegional);
            return !materiaRegionalId || materiaRegionalId === activeRegionalIdText;
          })
          : [];
        const activas = filtradas.filter((m) => (m.activo ?? m.Activo ?? true));
        setMateriasDisponibles(activas);
      } catch (error) {
        setMateriasError(error.message || 'No pudimos cargar las materias.');
      } finally {
        setMateriasLoading(false);
      }
    };

    fetchMaterias();
  }, [activeRegionalId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const summarizeAssociationResponses = (responses) => responses.reduce(
    (acc, res) => {
      const nuevos = res?.asociadosNuevos || res?.AsociadosNuevos || [];
      const reactivados = res?.reactivados || res?.Reactivados || [];
      const yaAsociados = res?.yaAsociados || res?.YaAsociados || [];
      const noEncontrados = res?.profesoresNoEncontrados || res?.ProfesoresNoEncontrados || [];
      const noRol = res?.profesoresNoRol || res?.ProfesoresNoRol || [];
      acc.nuevos += nuevos.length;
      acc.reactivados += reactivados.length;
      acc.yaAsociados += yaAsociados.length;
      acc.noEncontrados += noEncontrados.length;
      acc.noRol += noRol.length;
      return acc;
    },
    { nuevos: 0, reactivados: 0, yaAsociados: 0, noEncontrados: 0, noRol: 0 }
  );

  const formatAssociationSummary = (totals) => {
    const summaryParts = [];
    if (totals.nuevos) summaryParts.push(`Asociadas nuevas: ${totals.nuevos}`);
    if (totals.reactivados) summaryParts.push(`Reactivadas: ${totals.reactivados}`);
    if (totals.yaAsociados) summaryParts.push(`Ya asociadas: ${totals.yaAsociados}`);
    if (totals.noEncontrados) summaryParts.push(`No encontradas: ${totals.noEncontrados}`);
    if (totals.noRol) summaryParts.push(`Sin rol Profesor: ${totals.noRol}`);
    return summaryParts.join(' | ');
  };

  const reloadProfesorMaterias = async () => {
    const prof = await usuarioService.getProfesorById(profesorId, { idRegional: activeRegionalId });
    const loadedMaterias = normalizeMaterias(prof?.materias || prof?.Materias);
    setMateriasAsignadas(loadedMaterias);
    setPersistedMateriaIds(getLoadedMateriaIds(loadedMaterias));
    return loadedMaterias;
  };

  const persistPendingMaterias = async () => {
    if (!profesorId || pendingMateriaIds.length === 0) {
      return { hadPending: false, totals: summarizeAssociationResponses([]) };
    }

    const responses = await Promise.all(
      pendingMateriaIds.map((materiaId) => (
        materiaService.assignProfesores(materiaId, [profesorId], { idRegional: activeRegionalId })
      ))
    );
    const totals = summarizeAssociationResponses(responses);
    await reloadProfesorMaterias();
    return { hadPending: true, totals };
  };

  const handleAddMateria = (materiaId) => {
    const normalizedMateriaId = normalizeId(materiaId || selectedMateriaId);
    if (!normalizedMateriaId) return;
    const materia = materiasDisponibles.find((m) => getMateriaId(m) === normalizedMateriaId);
    if (!materia) return;
    if (materiasAsignadas.some((m) => getMateriaId(m) === normalizedMateriaId)) return;
    setMateriasAsignadas((prev) => [...prev, materia]);
    setSelectedMateriaId('');
    setMateriasMessage('');
    setMateriasMessageType('');
  };

  const handleRemoveMateria = async (materiaId) => {
    const normalizedMateriaId = normalizeId(materiaId);
    if (!normalizedMateriaId || !profesorId) return;

    if (!persistedMateriaIdSet.has(normalizedMateriaId)) {
      setMateriasAsignadas((prev) => prev.filter((m) => getMateriaId(m) !== normalizedMateriaId));
      setMateriasMessage('');
      setMateriasMessageType('');
      return;
    }

    if (!activeRegionalId) {
      setMateriasMessage('Selecciona un regional antes de desasociar la materia.');
      setMateriasMessageType('error');
      return;
    }

    setIsAssociatingMaterias(true);
    try {
      await materiaService.removeProfesores(normalizedMateriaId, [profesorId], { idRegional: activeRegionalId });
      setMateriasAsignadas((prev) => prev.filter((m) => getMateriaId(m) !== normalizedMateriaId));
      setPersistedMateriaIds((prev) => prev.filter((id) => normalizeId(id) !== normalizedMateriaId));
      setMateriasMessage('Materia desasociada correctamente.');
      setMateriasMessageType('success');
    } catch (error) {
      setMateriasMessage(error.message || 'No pudimos desasociar la materia.');
      setMateriasMessageType('error');
    } finally {
      setIsAssociatingMaterias(false);
    }
  };

  const handleAssignMaterias = async () => {
    if (!profesorId) return;
    if (!activeRegionalId) {
      setMateriasMessage('Selecciona un regional antes de asociar materias.');
      setMateriasMessageType('error');
      return;
    }
    if (materiasAsignadas.length === 0) {
      setMateriasMessage('Agrega al menos una materia para asociar.');
      setMateriasMessageType('error');
      return;
    }
    if (pendingMateriaIds.length === 0) {
      setMateriasMessage('No hay materias nuevas pendientes de asociar.');
      setMateriasMessageType('info');
      return;
    }

    setIsAssociatingMaterias(true);
    setMateriasMessage('Asociando materias al profesor...');
    setMateriasMessageType('info');
    try {
      const { totals } = await persistPendingMaterias();
      const summary = formatAssociationSummary(totals);
      setMateriasMessage(`Asociación completada. ${summary || 'Sin cambios nuevos.'}`.trim());
      setMateriasMessageType('success');
    } catch (error) {
      setMateriasMessage(error.message || 'No pudimos asociar las materias.');
      setMateriasMessageType('error');
    } finally {
      setIsAssociatingMaterias(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeRegionalId) {
      setMessage('Selecciona un regional antes de editar el profesor.');
      setMessageType('error');
      return;
    }
    if (!form.email) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    const dniValue = (form.dni || '').trim();
    if (!dniValue) {
      setMessage('Completa DNI para continuar.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage(pendingMateriaIds.length > 0 ? 'Guardando cambios y materias...' : 'Guardando cambios...');
    setMessageType('info');
    setMateriasMessage('');
    setMateriasMessageType('');
    let profesorUpdated = false;
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || form.email;
      await usuarioService.updateProfesor(profesorId, {
        ...form,
        displayName,
        idRegional: activeRegionalId,
        dni: dniValue,
        legajo: (form.legajo || '').trim() || null
      });
      profesorUpdated = true;

      let materiasSummary = '';
      if (pendingMateriaIds.length > 0) {
        setIsAssociatingMaterias(true);
        const { totals } = await persistPendingMaterias();
        materiasSummary = formatAssociationSummary(totals);
      }

      setMessage(`Profesor actualizado correctamente.${materiasSummary ? ` ${materiasSummary}` : ''}`);
      setMessageType('success');
      setTimeout(() => navigate('/admin-dashboard', { replace: true, state: { tab: 'profesores' } }), 1100);
    } catch (error) {
      setMessage(
        profesorUpdated
          ? `El profesor se actualizó, pero falló la asociación de materias.${error.message ? ` ${error.message}` : ''}`
          : (error.message || 'No pudimos actualizar el profesor.')
      );
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
      setIsAssociatingMaterias(false);
    }
  };

  return (
    <div className="new-dashboard admin-mode">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            activeItemId="profesores"
            onSelect={handleAdminMenuSelect}
          />
        </nav>
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Editar profesor</h1>
            <p className="page-subtitle">Actualiza los datos del docente asociado al regional {activeRegionalName}.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <UserAccountMenu />
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '1200px' }}>
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div className="content-card" style={{ flex: '1 1 520px', minWidth: '320px' }}>
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-edit" />
                <span>Datos del profesor</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Edita los datos del docente. El DNI es obligatorio y se usa como contraseña inicial; el legajo es opcional.
              </p>
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="profesor@ejemplo.com"
                  />
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input
                      type="text"
                      value={form.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      placeholder="Juan"
                    />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input
                      type="text"
                      value={form.apellido}
                      onChange={(e) => handleChange('apellido', e.target.value)}
                      placeholder="Perez"
                    />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      value={form.telefono}
                      onChange={(e) => handleChange('telefono', e.target.value)}
                      placeholder="123456"
                    />
                  </div>
                  <div className="form-field">
                    <label>DNI</label>
                    <input
                      type="text"
                      value={form.dni}
                      onChange={(e) => handleChange('dni', e.target.value)}
                      placeholder="30123456"
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Legajo (opcional)</label>
                  <input
                    type="text"
                    value={form.legajo}
                    onChange={(e) => handleChange('legajo', e.target.value)}
                    placeholder="A123"
                  />
                </div>
                <div className="form-field" style={{ padding: '0.75rem 0', borderTop: '1px solid var(--gray-200)', marginTop: '0.25rem' }}>
                  <label htmlFor="permiteCrearAsistente" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>
                    Permisos adicionales
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                    <input
                      type="checkbox"
                      id="permiteCrearAsistente"
                      checked={!!form.permiteCrearAsistente}
                      onChange={(e) => handleChange('permiteCrearAsistente', e.target.checked)}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <label htmlFor="permiteCrearAsistente" style={{ margin: 0 }}>
                      Permite crear asistentes
                    </label>
                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                      Habilita al docente a crear y probar asistentes IA.
                    </span>
                  </div>
                </div>

                {message && (
                  <div
                    className="modal-message"
                    style={{
                      marginTop: '0.5rem',
                      borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                      background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                      color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                    }}
                  >
                    {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => navigate('/admin-dashboard', { state: { tab: 'profesores' } })}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Guardar cambios</span>
                  </button>
                </div>
              </form>
            </div>
          </div>

            <div className="content-card professor-materias-card" style={{ flex: '1 1 360px', minWidth: '300px' }}>
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-book" />
                <span>Materias del profesor</span>
              </div>
            </div>
            <div className="card-content">
              <p style={{ marginTop: 0, color: '#475569' }}>
                Selecciona una o varias materias para asociar al profesor. Puedes desasociar materias desde esta vista.
              </p>

              {materiasLoading && <p>Cargando materias...</p>}
              {materiasError && <p style={{ color: '#b91c1c' }}>{materiasError}</p>}

              {!materiasLoading && materiasDisponibles.length > 0 && (
                <div className="form-field professor-subject-selector">
                  <label>Buscar materia</label>
                  <SearchableSelect
                    value={selectedMateriaId}
                    onChange={(value) => {
                      setSelectedMateriaId(value);
                      if (value) {
                        handleAddMateria(value);
                      }
                    }}
                    options={materiaSearchOptions}
                    placeholder="Escribe el nombre de la materia"
                    emptyMessage="No encontramos materias con esa búsqueda."
                    noOptionsMessage="No hay materias disponibles para agregar."
                    disabled={isSubmitting || isAssociatingMaterias}
                  />
                  {materiasDisponiblesParaAgregar.length === 0 && (
                    <p className="professor-subject-helper">
                      Todas las materias disponibles ya están agregadas a la lista.
                    </p>
                  )}
                </div>
              )}

              <div className="professor-subjects-section">
                <p className="professor-subjects-title">Materias asignadas</p>
                {materiasAsignadas.length === 0 && (
                  <p className="professor-subject-empty">Aún no hay materias asignadas.</p>
                )}
                <div className="professor-subject-chips">
                  {materiasAsignadas.map((materia) => {
                    const materiaId = getMateriaId(materia);
                    const nombre = getMateriaNombre(materia);
                    const isPending = materiaId && !persistedMateriaIdSet.has(materiaId);
                    return (
                      <div
                        key={materiaId}
                        className={`professor-subject-chip ${isPending ? 'is-pending' : ''}`}
                      >
                        <i className="fas fa-book" aria-hidden="true" />
                        <span>{nombre}</span>
                        {isPending && <span className="professor-subject-chip-status">Pendiente</span>}
                        <button
                          type="button"
                          onClick={() => handleRemoveMateria(materiaId)}
                          className="professor-subject-chip-remove"
                          disabled={isAssociatingMaterias || isSubmitting}
                          aria-label={`Quitar ${nombre}`}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {materiasMessage && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.75rem',
                    borderColor: materiasMessageType === 'success' ? '#bbf7d0' : materiasMessageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: materiasMessageType === 'success' ? '#dcfce7' : materiasMessageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: materiasMessageType === 'error' ? '#7f1d1d' : materiasMessageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isAssociatingMaterias && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {materiasMessage}
                </div>
              )}

              <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: '1.25rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignMaterias}
                  disabled={isAssociatingMaterias || isSubmitting || pendingMateriaIds.length === 0}
                >
                  {isAssociatingMaterias ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-link" />}
                  <span>{isAssociatingMaterias ? 'Asociando...' : 'Asociar materias pendientes'}</span>
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEditProfesor;
